import { z } from 'zod';

/**
 * One row of the CCD Export admin table. Each row is a hospitalization that has an
 * associated Avon note (`avonNoteId`) whose CCD XML can be downloaded.
 */
export interface CcdExportRowDto {
  hospitalizationId: number;
  avonNoteId: string;
  memberId: string;
  memberName: string;
  /** ISO date (yyyy-mm-dd) or null when not recorded. */
  admitDate: string | null;
  /** ISO date (yyyy-mm-dd) or null when not recorded. */
  dischargeDate: string | null;
  /** Epoch millis of the interaction that produced the Avon note, or null. */
  interactionDate: number | null;
}

export interface PaginatedCcdExportDto {
  rows: CcdExportRowDto[];
  count: number;
}

/**
 * Query params for the paginated CCD export list. Date-range presets
 * (Last 7 days, Last month, Custom) are resolved to epoch-millis bounds on the
 * client; the API only ever sees `startDate`/`endDate` timestamps applied to the
 * interaction date.
 */
export interface GetCcdExportListRequest {
  searchTerm?: string;
  startDate?: number;
  endDate?: number;
  orderDirection?: 'ASC' | 'DESC';
  'page-start'?: number;
  'page-size'?: number;
}

/**
 * Zod schema for the CCD export filter params shared by the list and zip-all
 * endpoints. Coerces query-string values to numbers.
 */
export const CcdExportFilterSchema = z.object({
  searchTerm: z.string().optional(),
  startDate: z.coerce.number().int().optional(),
  endDate: z.coerce.number().int().optional(),
  orderDirection: z.enum(['ASC', 'DESC']).optional(),
});

export type CcdExportFilter = z.infer<typeof CcdExportFilterSchema>;

/**
 * Builds the CCD XML filename for a given Avon note id: strips the leading
 * `note_` prefix and appends the fixed `000000000000` suffix, e.g.
 * `note_abc123` -> `abc123000000000000.xml`.
 */
export function buildCcdFileName(avonNoteId: string): string {
  return `${avonNoteId.replace(/^note_/, '')}000000000000.xml`;
}

/**
 * Bulk "Download all" guardrails. The zip endpoint fetches every matching CCD from
 * Avon into memory before streaming, so an unbounded range could exhaust memory or
 * time out. Bulk export therefore requires a bounded interaction-date range of at
 * most ~1 month, with a hard document-count cap as an out-of-memory backstop.
 */
export const CCD_ZIP_MAX_RANGE_DAYS = 31;
export const CCD_ZIP_MAX_DOCUMENTS = 500;

const CCD_ZIP_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Max allowed span in millis. Adds one day of slack so inclusive end-of-day bounds
 * and DST shifts on full-month selections aren't falsely rejected.
 */
export const CCD_ZIP_MAX_RANGE_MS =
  (CCD_ZIP_MAX_RANGE_DAYS + 1) * CCD_ZIP_DAY_MS;

/**
 * Whether a date range is valid for bulk CCD export: both bounds present and the
 * span within {@link CCD_ZIP_MAX_RANGE_MS}.
 */
export function isCcdZipRangeAllowed(
  startDate?: number,
  endDate?: number
): boolean {
  if (startDate == null || endDate == null) {
    return false;
  }
  return endDate - startDate <= CCD_ZIP_MAX_RANGE_MS;
}
